import { Reference } from '../utils/gatherReferences';
import {
  RawSQL,
  dangerousRawSql,
  sql,
  sqlF,
  sqlIdentifier,
  sqlJoinF,
} from '../../sqlTemplate';
import { Context, SqlError } from '../types';
import { convertEncodedUnits, exprToSql } from '../sql';
import assertNever from '../../utils/assertNever';
import { safeMaxStage } from '../utils';
import {
  PROGRAM_TABLE_I,
  debugParamsColI,
  mergeErrors,
  rowIdColI,
} from './internal';
import {
  sqlForDateOverlaps,
  sqlForDuplicateRows,
} from '../../referenceData/validationQueries';
import {
  STABLE_ROW_ID_COL,
  STABLE_ROW_ID_COL_I,
} from '../../referenceData/stableIds';
import { dateCastIfNotAlreadyCast } from './date_cast';

export type ReferenceSql = {
  // The name of this reference, used as e.g., a table name
  readonly name: string;
  // A SQL query that, when run, produces one row per error. Each row has a
  // single string column named "error".
  readonly preflight: string;
  // References are calculated using the help of a "lookup table." We use a
  // lookup table for three reasons:
  // - Even when there is a lot of data, there tends to be comparatively few
  //   unique values for reference data lookups
  // - Exact lookups are *much* more efficient than other sorts of boolean
  //   expressions (e.g., month ranges, fuzzy lookups)
  // - For references with fuzzy lookups, most approaches to calculating them
  //   will result in duplicating records in the left-hand (input) table (one
  //   per join candidate). Eliminating these duplicates is extremely costly.
  readonly lookupTable: {
    // A SQL query that, when run, creates a lookup table. This table contains
    // one row per unique combination of lookup keys, as well as the row ID of
    // the reference that matched (if any).
    table: string;
    // A SQL fragment like `LEFT JOIN ... ON ...` that looks up a value in the
    // lookup table. Uses the table above, and defines the relation used in the
    // expression below>
    join: string;
    // A SQL expression that, when run, returns the row ID as looked up in this
    // lookup table
    expr: string;
    // A SQL expression that, when run, returns a string representation of the
    // parameters used in this lookup
    debugExpr: string;
  };
  // A SQL fragment like `LEFT JOIN ... ON ...` that defines a table named
  // `name`. Either the lookup table (see above) query and join must be
  // performed prior to this join, or a join row ID must be provided (e.g., from
  // a previous execution).
  readonly join: string;
  // The error returned by this reference. See documentation for SqlError for
  // more.
  readonly error: SqlError | null;
  // The minimum stage the join can be run at. Note that because JOINs are the
  // first thing to run in a SQL query (i.e., before the SELECT part), the
  // minimum stage must be one *greater* than the stage of any of the matching
  // expressions. See the docs on SqlExpression for more on stages.
  readonly stage: number;
};

const THE_PROBLEM_WITH_DUPLICATES =
  'Duplicate rows result in ambiguity about which row should be used, and are therefore banned. You can fix this either by editing your reference data to remove the duplicate rows, or by adding additional matching criteria to your ref() call to resolve the ambiguity.';

// Pre-flight: prevent matching against reference data sources that might
// contain multiple join candidates. By checking for this error case early,
// we can construct considerably simpler code below, since we only need to
// guard against the case where a join produces zero results (which we can
// figure out by looking for NULL values in comparative isolation), and not
// the case in which a join produces two or more results (which we can only
// figure out by keeping a bunch of debug info around and by grouping the
// rows towards the end)
//
// This is a particularly great strategy because reference data tends to be
// really tiny (tens or hundreds of rows), and because we intend to push
// this check "upstream" soon (i.e., it may soon be invalid to publish
// reference data that contains duplicate "keys").
function preflightSqlForReference(ctx: Context, reference: Reference): string {
  const matchCols = Array.from(reference.kwargs.keys());
  const renderedLookupValues = matchCols.map(
    (c) => sqlF`' ${c}=' || COALESCE("${c}"::text, 'NULL') || `
  );

  // If the reference query doesn't care about dates, group on the matcher
  // columns and count to find violations.
  if (!reference.monthExpr) {
    return sql`
      SELECT 'For reference data "${reference.slug}" there are ' || __count || ' rows that have identical lookup values:' ||
        ${sqlJoinF(renderedLookupValues, `',' || `)}
        ' (row numbers: ' || __rows || '). ${THE_PROBLEM_WITH_DUPLICATES}' AS error
      FROM (
        ${sqlForDuplicateRows(reference.slug, matchCols)}
      )
    `;
  }

  // If the references query does contain dates, we want to find rows where the
  // matcher columns are the same AND the date ranges overlap. Parts of this
  // are similar to the date-indifferent query above, but with slight
  // differences.
  const matchColsWithDates = [...matchCols, 'start_date', 'end_date'];
  return sql`
    SELECT 'For reference data "${reference.slug}" there are ' || __count || ' rows that have identical lookup values during overlapping date ranges, as determined by start_date and end_date:' ||
      ${sqlJoinF(renderedLookupValues, `',' || `)}
      ' (row numbers: ' || __rows || '). ${THE_PROBLEM_WITH_DUPLICATES}' AS error
    FROM (
      ${sqlForDateOverlaps(reference.slug, matchColsWithDates)}
    )
  `;
}

export function referenceToSql(
  ctx: Context,
  reference: Reference
): ReferenceSql {
  const tname = sqlIdentifier(reference.name);

  // Map from ref data column name -> SQL expression that calculates the value
  // to match against, when executed in the "program" context
  const lookupTableKeys = new Map<string, RawSQL>();
  // The list of errors that were encountered while evaluating the arguments to
  // the reference
  const errors = new Array<SqlError | null>();
  // An array of join conditions (the part after the LEFT JOIN ... ON) when
  // computing the reference data join
  const joinConditions = new Array<RawSQL>();
  // An array of expressions valid in the ORDER BY context that prioritize which
  // record among potentially many matches should be used
  const priorities = new Array<RawSQL>();
  // An array of expressions that evaluate to strings, each of which describes
  // one of the matching parameters in a human-understandable way
  const debugParams = new Array<RawSQL>();
  // The maximum stage of any input into the join. The JOIN will run at this
  // stage + 1; see note above
  let stage = 0;

  if (reference.monthExpr) {
    const eSql = exprToSql(ctx, reference.monthExpr);
    stage = safeMaxStage([stage, eSql]);
    errors.push(eSql.error);
    lookupTableKeys.set('__month', dangerousRawSql(eSql.expr));

    joinConditions.push(
      sqlF`(refy.start_date IS NULL OR date_cast(refy.start_date) <= date_cast(keys.__month))`
    );
    joinConditions.push(
      sqlF`(refy.end_date IS NULL OR date_cast(keys.__month) < date_cast(refy.end_date))`
    );
    debugParams.push(
      sqlF`'start_date <= ' || COALESCE(date_cast(keys.__month)::text, '(null)') || ' < end_date'`
    );
  }

  for (const [arg, { t, expr }] of reference.kwargs) {
    const eSql = exprToSql(ctx, expr);
    stage = safeMaxStage([stage, eSql]);
    errors.push(eSql.error);
    let sqlExpr = dangerousRawSql(eSql.expr);
    lookupTableKeys.set(arg, sqlExpr);

    const argI = sqlIdentifier(arg);
    let refColumn = sqlF`refy.${argI}`;
    // We've had a bad history of dates being in strange formats here (e.g.,
    // epoch milliseconds, ISO strings, yearmonths, actual date types). In
    // theory we ought to address these issues at the boundaries of the system
    // (i.e., reference data schemas, BART schemas, etc.), but we took the "big
    // hammer" whackamole approach. We can and should fix this eventually, but
    // for the time being we're stuck with this.
    const type = ctx.refTypes.get(reference.slug)?.get(arg);
    if (type && (type.ty === 'date' || type.ty === 'datetime')) {
      refColumn = sqlF`date_cast(${refColumn})`;
      sqlExpr = dateCastIfNotAlreadyCast(sqlExpr);
    }

    switch (t) {
      case 'exact':
        joinConditions.push(
          sqlF`${refColumn} IS NOT DISTINCT FROM keys.${argI}`
        );
        debugParams.push(
          sqlF`'${arg}=' || COALESCE(keys.${argI}::text, '(null)')`
        );
        break;
      case 'fuzzy':
        joinConditions.push(
          sqlF`(${refColumn} IS NOT DISTINCT FROM keys.${argI} OR ${refColumn} IS NULL)`
        );
        priorities.push(sqlF`${refColumn} IS NULL`);
        debugParams.push(
          sqlF`'${arg}~' || COALESCE(keys.${argI}::text, '(null)')`
        );
        break;
      default:
        assertNever(t);
    }
  }

  const rowIdExpr = sqlF`${sqlIdentifier(reference.slug)}.${sqlIdentifier(
    STABLE_ROW_ID_COL
  )}`;

  const rawReferenceTable = sqlF`
    SELECT
      *,
      ${rowIdExpr} AS __row_id
    FROM ${sqlIdentifier(reference.slug)}
  `;

  const rowType = ctx.refTypes.get(reference.slug);
  const referenceTable = rowType
    ? convertEncodedUnits(rawReferenceTable, rowType)
    : rawReferenceTable;

  const join = sql`
    LEFT JOIN (${referenceTable}) AS ${tname}
      ON ${tname}.__row_id = ${PROGRAM_TABLE_I}.${rowIdColI(reference.name)}`;

  const lutI = sqlIdentifier(`${reference.name}__lut`);

  if (lookupTableKeys.size === 0) {
    // Special case: if this reference doesn't have any matching parameters,
    // there's not much to do here. We handle it separately because it's hard to
    // make the SQL queries below scale down to 0 items.
    return {
      name: reference.name,
      preflight: preflightSqlForReference(ctx, reference),
      lookupTable: {
        // TODO(carl): sort of stupid we have to do this, but I don't want to
        // futz with the types for this one strange edge case
        table: sql`
          CREATE OR REPLACE TEMP TABLE ${lutI} AS SELECT
            COALESCE(${STABLE_ROW_ID_COL_I}, 'not a valid id') AS __row_id
          FROM ${sqlIdentifier(reference.slug)}
        `,
        join: sql`LEFT JOIN ${lutI} ON TRUE`,
        expr: sql`${lutI}.__row_id`,
        debugExpr: `'(no parameters)'`,
      },
      join,
      error: {
        exists: sql`${rowIdColI(reference.name)} IS NULL`,
        expr: sql`'when looking up reference data for "${reference.slug}" without parameters there were no matches'`,
      },
      stage: 1,
    };
  }

  const lutKeys = Array.from(lookupTableKeys.entries()).map(([k, v]) => [
    sqlIdentifier(k),
    v,
  ]);
  /*
   * This is the query we're targeting for the lookup table:
   *
   * WITH keys AS (
   *   SELECT DISTINCT
   *     expr1 AS key1,
   *     expr2 AS key2,
   *     ...
   *   FROM __p
   * ), refy AS (
   *   SELECT
   *     key1, key2, ...,
   *     __stable_row_id AS __row_id
   *   FROM "tname"
   * )
   * SELECT DISTINCT ON (key1, key2, ...)
   *   keys.key1, keys.key2, ...
   *   refy.__row_id
   * FROM keys LEFT JOIN refy
   *   ON keys.key1 = refy.key1
   *   AND (keys.key2 = refy.key2 OR refy.key2 IS NULL) -- fuzzy
   *   ...
   * ORDER BY key1, key2, ..., refy.key1 IS NULL, refy.key2 IS NULL, ...
   */
  const keys = sqlF`SELECT DISTINCT ${sqlJoinF(
    lutKeys.map(([key, expr]) => sqlF`${expr} AS ${key}`),
    ', '
  )} FROM ${PROGRAM_TABLE_I}`;
  const refy = sqlF`SELECT *, ${STABLE_ROW_ID_COL_I} AS __row_id FROM ${sqlIdentifier(
    reference.slug
  )}`;
  const keyList = sqlJoinF(
    lutKeys.map(([key, _]) => sqlF`keys.${key}`),
    ', '
  );
  const distinctOn = sqlF`SELECT DISTINCT ON (${keyList}) ${keyList}, refy.__row_id, CONCAT_WS(', ', ${sqlJoinF(
    debugParams,
    ', '
  )}) AS __debug_params FROM keys LEFT JOIN refy ON ${
    joinConditions.length > 0 ? sqlJoinF(joinConditions, ' AND ') : true
  } ORDER BY ${keyList}, ${
    priorities.length > 0 ? sqlJoinF(priorities, ', ') : true
  }`;

  const lutJoin = sql`LEFT JOIN ${lutI} ON ${sqlJoinF(
    lutKeys.map(([k, v]) => sqlF`${lutI}.${k} IS NOT DISTINCT FROM ${v}`),
    ' AND '
  )}`;

  const lookupTable = {
    table: sql`CREATE OR REPLACE TEMP TABLE ${lutI} AS (WITH keys AS (${keys}), refy AS (${refy}) ${distinctOn})`,
    join: lutJoin,
    expr: sql`${lutI}.__row_id`,
    debugExpr: sql`${lutI}.__debug_params`,
  };

  return {
    name: reference.name,
    preflight: preflightSqlForReference(ctx, reference),
    lookupTable,
    join,
    error: mergeErrors([
      ...errors,
      {
        exists: sql`${rowIdColI(reference.name)} IS NULL`,
        expr: sql`'when looking up reference data for "${
          reference.slug
        }" with parameters ' || ${debugParamsColI(
          reference.name
        )} || ' there were no matches'`,
      },
    ]),
    stage: stage + 1,
  };
}
